import { Link, useNavigate } from 'react-router-dom';
import { Phone, Mail, MapPin, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ContactPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Navigation */}
      <nav className="border-b border-stone-100 bg-white/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-18 py-4">
            <Link to="/" className="flex items-center gap-2.5" data-testid="logo">
              <div className="w-9 h-9 rounded-xl bg-[#2F5233] flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <span 
                className="font-semibold text-xl text-stone-900 tracking-tight"
                style={{ fontFamily: 'Fraunces, Georgia, serif' }}
              >
                ClarityStack
              </span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <Link to="/#features" className="text-sm font-medium text-stone-600 hover:text-[#2F5233] transition-colors">
                Features
              </Link>
              <Link to="/pricing" className="text-sm font-medium text-stone-600 hover:text-[#2F5233] transition-colors">
                Pricing
              </Link>
              <Link to="/contact" className="text-sm font-medium text-[#2F5233] transition-colors">
                Contact
              </Link>
            </div>
            
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                className="text-stone-600 hover:text-stone-900 font-medium"
              >
                Login
              </Button>
              <Button 
                className="bg-[#2F5233] hover:bg-[#243f28] text-white rounded-full px-6 font-medium shadow-sm"
                onClick={() => navigate('/register')}
              >
                Start Free Trial
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="py-16 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h1 
            className="text-4xl font-semibold text-stone-900 mb-3"
            style={{ fontFamily: 'Fraunces, Georgia, serif' }}
          >
            Contact Us
          </h1>
          <p className="text-stone-500 mb-12">
            ClarityStack — A product of Think Through Enterprise
          </p>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 
                  className="text-xl font-semibold text-stone-900 mb-4"
                  style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                >
                  Get in Touch
                </h2>
                <p className="text-stone-600 leading-relaxed">
                  Have questions about ClarityStack? We&apos;re here to help.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4 p-5 bg-stone-50 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-[#2F5233]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-[#2F5233]" />
                  </div>
                  <div>
                    <p className="font-medium text-stone-900 mb-1">Address</p>
                    <p className="text-stone-600 text-sm leading-relaxed">
                      B104 Shantideep Society, Sathya Sai Complex,<br />
                      Chikuwadi, Borivali West,<br />
                      Mumbai – 400092
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 bg-stone-50 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-[#2F5233]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="h-5 w-5 text-[#2F5233]" />
                  </div>
                  <div>
                    <p className="font-medium text-stone-900 mb-1">Phone</p>
                    <a href="tel:+919967124157" className="text-stone-600 text-sm hover:text-[#2F5233] transition-colors">
                      +91 9967124157
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 bg-stone-50 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-[#2F5233]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-[#2F5233]" />
                  </div>
                  <div>
                    <p className="font-medium text-stone-900 mb-1">Email</p>
                    <a href="mailto:support@claritystack.live" className="text-stone-600 text-sm hover:text-[#2F5233] transition-colors">
                      support@claritystack.live
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Support Hours */}
            <div className="space-y-8">
              <div>
                <h2 
                  className="text-xl font-semibold text-stone-900 mb-4"
                  style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                >
                  Support Hours
                </h2>
                <div className="p-5 bg-stone-50 rounded-xl space-y-3">
                  <p className="text-stone-600 text-sm">
                    <span className="font-medium text-stone-900">Monday – Friday:</span><br />
                    10:00 AM – 6:00 PM IST
                  </p>
                  <p className="text-stone-600 text-sm">
                    <span className="font-medium text-stone-900">Saturday:</span><br />
                    10:00 AM – 2:00 PM IST
                  </p>
                  <p className="text-stone-600 text-sm">
                    <span className="font-medium text-stone-900">Sunday:</span><br />
                    Closed
                  </p>
                </div>
              </div>

              <div>
                <h2 
                  className="text-xl font-semibold text-stone-900 mb-4"
                  style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                >
                  Response Time
                </h2>
                <p className="text-stone-600 text-sm leading-relaxed">
                  We aim to respond to all inquiries within 24–48 business hours.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-16 px-6 lg:px-8 bg-stone-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-9 h-9 rounded-xl bg-[#2F5233] flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <span 
                  className="font-semibold text-xl tracking-tight"
                  style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                >
                  ClarityStack
                </span>
              </div>
              <p className="text-stone-400 text-sm mb-6">
                ClarityStack is a product of Think Through Enterprise
              </p>
              <div className="text-sm text-stone-400 space-y-2">
                <p className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-stone-500" />
                  <span>B104 Shantideep Society, Sathya Sai Complex,<br />Chikuwadi, Borivali West, Mumbai – 400092</span>
                </p>
                <p className="flex items-center gap-3">
                  <Phone className="h-4 w-4 flex-shrink-0 text-stone-500" />
                  <span>+91 9967124157</span>
                </p>
                <p className="flex items-center gap-3">
                  <Mail className="h-4 w-4 flex-shrink-0 text-stone-500" />
                  <span>support@claritystack.live</span>
                </p>
              </div>
            </div>
            
            {/* Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/#features" className="text-stone-400 hover:text-white transition-colors">Features</Link></li>
                <li><Link to="/pricing" className="text-stone-400 hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/contact" className="text-stone-400 hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/privacy" className="text-stone-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-stone-400 hover:text-white transition-colors">Terms & Conditions</Link></li>
                <li><Link to="/refund" className="text-stone-400 hover:text-white transition-colors">Refund Policy</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-stone-800">
            <p className="text-sm text-stone-500 text-center">
              © {new Date().getFullYear()} Think Through Enterprise. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
